import { Bookmark, EyeOff, Loader2, LocateFixed, MapPin, RefreshCw, Radio, Plane } from "lucide-react";

/**
 * Floating top pill: title + counts on the left, action buttons on the right
 * (Live-only, Visitors, Desires, Saved, Ghost, Refresh, Recenter). Kept as a pure
 * presentational strip — all state lives in Nearby.jsx and comes down as props.
 */
export default function NearbyHeader({
  filteredCount,
  radiusMi,
  liveCount,
  liveOnly,
  onToggleLiveOnly,
  desireCount,
  onToggleDesires,
  savedCount,
  onToggleSaved,
  onRefresh,
  refreshing,
  ghostMode,
  onToggleGhost,
  onRecenter,
  mode,
  onToggleMode,
}) {
  const visitorsOn = mode === "visitors";
  return (
    <div className="pointer-events-none absolute top-4 inset-x-4 z-[1200] flex justify-between items-start gap-3">
      <div className="pointer-events-auto flex items-center gap-2 bg-black/70 backdrop-blur-md border border-white/10 rounded-full pl-2 pr-4 py-1.5">
        <MapPin size={16} className={visitorsOn ? "text-sky-400" : "text-rose-500"} />
        <div>
          <div className="text-white font-display text-lg leading-none flex items-center gap-1.5">
            {visitorsOn ? "Visitors" : "Social Meet"}
            {ghostMode && (
              <span
                data-testid="nearby-header-ghost-chip"
                title="You're invisible — nobody sees your pin"
                className="text-[9px] uppercase tracking-widest bg-violet-500/25 border border-violet-400/50 text-violet-200 rounded-full px-1.5 py-0.5 flex items-center gap-1"
              >
                <EyeOff size={9} /> Ghost
              </span>
            )}
          </div>
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 mt-0.5">
            {visitorsOn
              ? `${filteredCount} travelers in ${radiusMi} mi`
              : `${filteredCount} in ${radiusMi} mi · ${liveCount} live`}
          </div>
        </div>
      </div>
      <div className="pointer-events-auto flex items-center gap-2 flex-wrap justify-end">
        <button
          data-testid="nearby-mode-toggle"
          onClick={onToggleMode}
          className={`flex items-center gap-1.5 rounded-full px-3 py-2 text-xs uppercase tracking-widest font-semibold border transition ${
            visitorsOn
              ? "bg-sky-500 border-sky-400 text-white shadow-lg shadow-sky-500/30"
              : "bg-black/70 backdrop-blur-md border-white/10 text-white"
          }`}
          title={visitorsOn ? "Show local members" : "Show travelers currently visiting"}
        >
          <Plane size={13} />
          {visitorsOn ? "Visitors · on" : "Visitors"}
        </button>
        <button
          data-testid="nearby-live-only-toggle"
          onClick={onToggleLiveOnly}
          disabled={visitorsOn}
          className={`flex items-center gap-1.5 rounded-full px-3 py-2 text-xs uppercase tracking-widest font-semibold border transition disabled:opacity-40 disabled:cursor-not-allowed ${
            liveOnly && !visitorsOn
              ? "bg-rose-600 border-rose-500 text-white shadow-lg shadow-rose-500/30 animate-pulse"
              : "bg-black/70 backdrop-blur-md border-white/10 text-white"
          }`}
          title={liveOnly ? "Show everyone" : "Show only live streams"}
        >
          <Radio size={13} />
          {liveOnly ? "Live only · on" : "Live only"}
        </button>
        <button
          data-testid="nearby-desires-toggle"
          onClick={onToggleDesires}
          className={`flex items-center gap-1.5 rounded-full px-3 py-2 text-xs uppercase tracking-widest font-semibold border transition ${
            desireCount > 0
              ? "bg-rose-600 border-rose-500 text-white shadow-lg shadow-rose-500/25"
              : "bg-black/70 backdrop-blur-md border-white/10 text-white"
          }`}
          title="Filter by shared desires"
        >
          ✨ Desires{desireCount > 0 ? ` · ${desireCount}` : ""}
        </button>
        <button
          data-testid="nearby-saved-toggle"
          onClick={onToggleSaved}
          className="flex items-center gap-1.5 bg-black/70 backdrop-blur-md border border-white/10 rounded-full px-3 py-2 text-white text-sm"
          title="Saved searches"
        >
          <Bookmark size={14} />
          {savedCount > 0 && (
            <span className="text-[10px] bg-rose-600 text-white rounded-full px-1.5">{savedCount}</span>
          )}
        </button>
        <button
          data-testid="nearby-ghost-toggle"
          onClick={onToggleGhost}
          className={`w-10 h-10 rounded-full border flex items-center justify-center transition ${
            ghostMode
              ? "bg-violet-600/85 border-violet-400 text-white shadow-lg shadow-violet-500/30"
              : "bg-black/70 backdrop-blur-md border-white/10 text-white hover:border-violet-400"
          }`}
          title={ghostMode
            ? "Ghost mode ON — nobody sees your pin. Tap to reveal yourself."
            : "Go ghost — hide your pin from everyone else"}
        >
          <EyeOff size={16} />
        </button>
        <button
          data-testid="nearby-refresh"
          onClick={onRefresh}
          disabled={refreshing}
          className="w-10 h-10 rounded-full bg-black/70 backdrop-blur-md border border-white/10 text-white flex items-center justify-center hover:border-rose-500 transition disabled:opacity-60"
          title="Refresh pins"
        >
          {refreshing ? <Loader2 size={16} className="animate-spin" /> : <RefreshCw size={16} />}
        </button>
        <button
          data-testid="nearby-recenter"
          onClick={onRecenter}
          className="w-10 h-10 rounded-full bg-black/70 backdrop-blur-md border border-white/10 text-white flex items-center justify-center hover:border-rose-500 transition"
          title="Re-detect my location"
        >
          <LocateFixed size={18} />
        </button>
      </div>
    </div>
  );
}
